import { useTranslation } from 'react-i18next';
import { useCallback, useState } from 'react';
import { DocumentationThunks } from 'modules/documentationPage/redux';
import {
  validateFileNameSize,
  validateFileSize,
  validateFileType,
} from '../utils/validationHelpers';
import { removeDuplicates } from '../documentationPage/components/helpers';
import { useAppDispatch } from './appHooks';
import { DocumentCategory, DocumentDetails } from '../types/Document';

export const useFileManagement = (
  setErrorMessage: (error: string) => void,
  maxFiles = 1,
) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [files, setFiles] = useState<File[]>([]);

  const addFile = useCallback(
    (e: any) => {
      const tempFiles: File[] = [];
      let keys;
      let uploadedFiles: { [x: string]: File };

      if (e.type === 'drop') {
        e.preventDefault();
        keys = Object.keys(e.dataTransfer.files);
        uploadedFiles = e.dataTransfer.files;
      } else {
        keys = Object.keys(e.target.files);
        uploadedFiles = e.target.files;
      }

      let hasError = false;

      if (files.length + keys.length > maxFiles) {
        setErrorMessage(t('documentationPage.maxFilesMessage'));
        hasError = true;
        return;
      }

      keys.forEach((key) => {
        if (!validateFileNameSize(uploadedFiles[key])) {
          setErrorMessage(t('documentationPage.nameTooLongMessage'));
          hasError = true;
        }
        if (!validateFileSize(uploadedFiles[key])) {
          setErrorMessage(t('documentationPage.fileTooBigMessage'));
          hasError = true;
        }
        if (!validateFileType(uploadedFiles[key])) {
          setErrorMessage(t('documentationPage.wrongFormatMessage'));
          hasError = true;
        }
        if (
          validateFileNameSize(uploadedFiles[key]) &&
          validateFileSize(uploadedFiles[key]) &&
          validateFileType(uploadedFiles[key])
        ) {
          tempFiles.push(uploadedFiles[key]);
        }
      });
      if (!hasError) setErrorMessage('');
      const newFileList = removeDuplicates([...(files ?? []), ...tempFiles]);
      setFiles(newFileList);
      e.target.value = '';
    },
    [t, setErrorMessage, setFiles, files, maxFiles],
  );

  const removeFile = useCallback(
    (name: string) => {
      const newFileList = [...(files ?? [])].filter(
        (file) => !(file.name === name),
      );
      setFiles(newFileList);
    },
    [files, setFiles],
  );

  const handleUpload = useCallback(
    (documentCategory: DocumentCategory, documentDetails: DocumentDetails) => {
      if (!files.length) {
        setErrorMessage(t('documentationPage.emptyFileListMessage'));
      } else {
        const formData = new FormData();

        files?.forEach((file) => formData.append('files', file));
        Object.keys(documentDetails).map((key) =>
          formData.append(
            key,
            documentDetails[key as keyof typeof documentDetails] || '',
          ),
        );

        dispatch(
          DocumentationThunks.uploadDocuments({
            formData,
            documentCategory,
          }),
        );
      }
    },
    [dispatch, t, setErrorMessage, files],
  );

  return { files, setFiles, addFile, removeFile, handleUpload };
};
